import React, { useMemo } from 'react';
import AdminPageShell from '@/components/admin/AdminPageShell';
import AdminVideoCreateAccepted from '@/components/admin/videoCreate/AdminVideoCreateAccepted';
import AdminVideoCreateConfirm from '@/components/admin/videoCreate/AdminVideoCreateConfirm';
import AdminVideoCreateForm from '@/components/admin/videoCreate/AdminVideoCreateForm';
import AdminVideoCreateProgress from '@/components/admin/videoCreate/AdminVideoCreateProgress';
import AdminVideoUploadJobList from '@/components/admin/videoCreate/AdminVideoUploadJobList';
import { getAdminFeature } from '@/config/adminFeatures';
import { useAdminVideoCreate } from '@/hooks/useAdminVideoCreate';
import { useRequireAdminRedirect } from '@/hooks/useRequireAdminRedirect';

const feature = getAdminFeature('AdminVideoCreate');

const STEPS = [
  { key: 'form', label: '情報入力' },
  { key: 'confirm', label: '内容確認' },
  { key: 'uploading', label: 'アップロード' },
  { key: 'accepted', label: '受付完了' },
];

function StepIndicator({ currentStep }) {
  const currentIndex = Math.max(
    0,
    STEPS.findIndex((step) => step.key === currentStep)
  );

  return (
    <ol className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      {STEPS.map((step, index) => {
        const isCurrent = index === currentIndex;
        const isDone = index < currentIndex;

        return (
          <li key={step.key} className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-medium ${
                isCurrent
                  ? 'bg-[#8b6f47] text-[#f5ede3]'
                  : isDone
                    ? 'bg-[#8b6f47]/15 text-[#5d4529]'
                    : 'bg-white text-[#a08e77]'
              }`}
            >
              <span>{index + 1}</span>
              {step.label}
            </span>
            {index < STEPS.length - 1 && <span className="text-[#d4c4b0]">›</span>}
          </li>
        );
      })}
    </ol>
  );
}

export default function AdminVideoCreate() {
  const { canRenderProtectedContent, isLoadingAuth } = useRequireAdminRedirect();
  const create = useAdminVideoCreate({ enabled: canRenderProtectedContent });

  const selectedCategoryName = useMemo(() => {
    const selected = create.categories.find(
      (category) => String(category.id) === String(create.form.categoryId)
    );
    if (!selected) return null;
    return selected.slug ? `${selected.name}（${selected.slug}）` : selected.name;
  }, [create.categories, create.form.categoryId]);

  if (isLoadingAuth || !canRenderProtectedContent) return null;

  return (
    <AdminPageShell
      icon={feature.icon}
      title={feature.label}
      description="動画・サムネイル・配布資料をアップロードし、videos テーブルへ登録します。"
      containerClassName="max-w-3xl"
    >
      <StepIndicator currentStep={create.step} />

      {create.step === 'form' && (
        <div className="space-y-4">
          <AdminVideoCreateForm
            form={create.form}
            errors={create.errors}
            thumbnailPreviewUrl={create.thumbnailPreviewUrl}
            categories={create.categories}
            categoriesLoading={create.categoriesLoading}
            categoriesError={create.categoriesError}
            onRetryCategories={create.refetchCategories}
            onFileChange={create.setFile}
            onFieldChange={create.setField}
            onSubmit={create.goToConfirm}
          />
          <AdminVideoUploadJobList enabled={canRenderProtectedContent} />
        </div>
      )}

      {create.step === 'confirm' && (
        <AdminVideoCreateConfirm
          form={create.form}
          thumbnailPreviewUrl={create.thumbnailPreviewUrl}
          categoryName={selectedCategoryName}
          onBack={create.backToForm}
          onConfirm={create.startUpload}
        />
      )}

      {create.step === 'uploading' && (
        <AdminVideoCreateProgress
          phase={create.phase}
          uploadProgress={create.uploadProgress}
          hasMaterial={Boolean(create.form.materialFile)}
          errorMessage={create.errorMessage}
          onBackToConfirm={create.backToForm}
        />
      )}

      {create.step === 'accepted' && (
        <AdminVideoCreateAccepted
          result={create.result}
          phase={create.phase}
          transcodeProgress={create.transcodeProgress}
          errorMessage={create.errorMessage}
          pollErrorMessage={create.pollErrorMessage}
          onCreateAnother={create.reset}
        />
      )}
    </AdminPageShell>
  );
}
